import {
	wrapper,
	createApiClient,
	PaginationParams,
	Result,
	ApiError,
} from "../base";
import EnvironementDO = App.Data.EnvironmentDO;
import ProductPlanDO = App.Data.ProductPlanDO;
import PermissionStructureDTO = App.Data.PermissionStructureDTO;
import LanguageDO = App.Data.LanguageDO;
import UpdatePermissionDTO = App.Data.UpdatePermissionDTO;
import BulkUpdatePermissionDTO = App.Data.BulkUpdatePermissionDTO;
import NotificationEventDO = App.Data.NotificationEventDO;
import PermissionDO = App.Data.PermissionDO;
import AuthenticationModuleDO = App.Data.AuthenticationModuleDO;
import ColorPaletteDO = App.Data.ColorPaletteDO;
import AiPromptDO = App.Data.AiPromptDO;

const basePath = `environment`;
const baseClient = createApiClient();

export default class {
	getEnvironment(): Promise<Result<EnvironementDO, ApiError>> {
		return wrapper(baseClient.get(`${basePath}`));
	}
	/** Unauthenticated variant used by the Gotenberg render page, guarded by the check-gotenberg-ip middleware. */
	getEnvironmentForRender(): Promise<Result<EnvironementDO, ApiError>> {
		return wrapper(baseClient.get(`gotenberg/${basePath}`));
	}
	getEnvironmentPermissions(): Promise<Result<PermissionDO[], ApiError>> {
		return wrapper(baseClient.get(`${basePath}/permissions`));
	}
	getAuthenticationRequirements(): Promise<
		Result<App.Data.EnvironmentAuthenticationRequirementsDTO, ApiError>
	> {
		return wrapper(baseClient.get(`authentication-requirements`));
	}
	getAvailableNotificationEvents(): Promise<
		Result<NotificationEventDO[], ApiError>
	> {
		return wrapper(baseClient.get(`${basePath}/notification-events`));
	}
	createNotificationEvent(
		notificationEventDO: NotificationEventDO
	): Promise<Result<NotificationEventDO, ApiError>> {
		return wrapper(
			baseClient.post(`${basePath}/notification-event`, notificationEventDO)
		);
	}
	updateNotificationEvent(
		notificationEventDO: NotificationEventDO
	): Promise<Result<NotificationEventDO, ApiError>> {
		return wrapper(
			baseClient.put(
				`${basePath}/notification-event/${notificationEventDO.uuid}`,
				notificationEventDO
			)
		);
	}
	removeNotificationEvent(
		uuid: string
	): Promise<Result<NotificationEventDO, ApiError>> {
		return wrapper(baseClient.delete(`${basePath}/notification-event/${uuid}`));
	}
	updateEnvironment(
		environment: EnvironementDO
	): Promise<Result<EnvironementDO, ApiError>> {
		return wrapper(
			baseClient.put(`${basePath}/${environment.uuid}`, environment)
		);
	}
	updateEnvironmentPermission(
		updatePermissionDTO: UpdatePermissionDTO
	): Promise<Result<EnvironementDO, ApiError>> {
		return wrapper(
			baseClient.put(
				`${basePath}/updatePermission/${updatePermissionDTO.name}`,
				updatePermissionDTO
			)
		);
	}

	bulkUpdateEnvironmentPermission(
		dto: BulkUpdatePermissionDTO
	): Promise<Result<string, ApiError>> {
		return wrapper(baseClient.put(`${basePath}/bulkUpdatePermission`, dto));
	}
	updateEnvironmentProperty(
		UpdatePropertyDTO: App.Data.UpdatePropertyDTO
	): Promise<Result<EnvironementDO, ApiError>> {
		return wrapper(
			baseClient.put(
				`${basePath}/updateProperty/${UpdatePropertyDTO.name_clean}`,
				UpdatePropertyDTO
			)
		);
	}
	getProductPlans(): Promise<Result<ProductPlanDO[], ApiError>> {
		return wrapper(baseClient.get(`${basePath}/getProductPlans`));
	}
	getPermissionStructure(): Promise<Result<PermissionStructureDTO, ApiError>> {
		return wrapper(baseClient.get(`${basePath}/getPermissionStructure`));
	}
	getLanguages(): Promise<Result<LanguageDO[], ApiError>> {
		return wrapper(baseClient.get(`${basePath}/getLanguages`));
	}
	getAuthenticationModules(): Promise<
		Result<AuthenticationModuleDO[], ApiError>
	> {
		return wrapper(baseClient.get(`${basePath}/authentication/index-modules`));
	}
	createAuthenticationModule(
		authenticationModule: AuthenticationModuleDO
	): Promise<Result<AuthenticationModuleDO[], ApiError>> {
		return wrapper(
			baseClient.post(`${basePath}/authentication/module`, authenticationModule)
		);
	}
	updateAuthenticationModule(
		authenticationModule: AuthenticationModuleDO
	): Promise<Result<AuthenticationModuleDO[], ApiError>> {
		return wrapper(
			baseClient.put(
				`${basePath}/authentication/module/${authenticationModule.uuid}`,
				authenticationModule
			)
		);
	}
	removeAuthenticationModule(
		authenticationModule: AuthenticationModuleDO
	): Promise<Result<AuthenticationModuleDO[], ApiError>> {
		return wrapper(
			baseClient.delete(
				`${basePath}/authentication/module/${authenticationModule.uuid}`
			)
		);
	}
	getColorPalettes(): Promise<Result<ColorPaletteDO[], ApiError>> {
		return wrapper(baseClient.get(`${basePath}/color-palettes`));
	}
	getColorPalette(uuid: string): Promise<Result<ColorPaletteDO, ApiError>> {
		return wrapper(baseClient.get(`${basePath}/color-palette/${uuid}`));
	}
	createColorPalette(
		colorPalette: ColorPaletteDO
	): Promise<Result<ColorPaletteDO, ApiError>> {
		return wrapper(baseClient.post(`${basePath}/color-palette`, colorPalette));
	}
	updateColorPalette(
		colorPalette: ColorPaletteDO
	): Promise<Result<ColorPaletteDO, ApiError>> {
		return wrapper(
			baseClient.put(
				`${basePath}/color-palette/${colorPalette.uuid}`,
				colorPalette
			)
		);
	}
	deleteColorPalette(uuid: string): Promise<Result<string, ApiError>> {
		return wrapper(baseClient.delete(`${basePath}/color-palette/${uuid}`));
	}
	getAiPrompts(): Promise<Result<AiPromptDO[], ApiError>> {
		return wrapper(baseClient.get(`${basePath}/ai-prompts`));
	}
	createAiPrompt(aiPrompt: AiPromptDO): Promise<Result<AiPromptDO, ApiError>> {
		return wrapper(baseClient.post(`${basePath}/ai-prompts`, aiPrompt));
	}
	updateAiPrompt(aiPrompt: AiPromptDO): Promise<Result<AiPromptDO, ApiError>> {
		return wrapper(
			baseClient.put(`${basePath}/ai-prompts/${aiPrompt.id}`, aiPrompt)
		);
	}
}
